import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Container, Typography, Paper, Button, AppBar, Toolbar } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import logo from '../../assets/images/big-logo.svg';

type LegalTextType = 'kvkk' | 'satis' | 'gizlilik' | 'cerez' | 'iptal-iade';

const legalTextMap: Record<LegalTextType, string> = {
  'kvkk': 'KVKK ve Kişisel Verilerin Korunması Politikası',
  'satis': 'Mesafeli Satış Sözleşmesi',
  'gizlilik': 'Gizlilik Politikası',
  'cerez': 'Çerez Politikası',
  'iptal-iade': 'İptal ve İade Koşulları'
};

export default function LegalText() {
  const { type } = useParams<{ type: LegalTextType }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  
  // Login olup olmadığını kontrol et
  const isLoggedIn = location.pathname.includes('/legal/') && !location.pathname.includes('/login');

  useEffect(() => {
    if (!type || !legalTextMap[type]) {
      setContent('Yasal metin bulunamadı.');
      setLoading(false);
      return;
    }

    const fetchLegalText = async () => {
      try {
        const response = await fetch(`/legal-texts/${type}.html`);
        if (response.ok) {
          const htmlContent = await response.text();
          setContent(htmlContent);
        } else {
          setContent('Yasal metin yüklenirken bir hata oluştu.');
        }
      } catch (error) {
        setContent('Yasal metin yüklenirken bir hata oluştu.');
      } finally {
        setLoading(false);
      }
    };

    fetchLegalText();
  }, [type]);

  if (loading) {
    return (
      <>
        <AppBar position="static" sx={{ backgroundColor: 'rgba(2, 36, 38, 1)' }}>
          <Toolbar>
            <Box component="img" src={logo} alt="logo" sx={{ height: 40, mr: 2 }} />
          </Toolbar>
        </AppBar>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Typography>Yükleniyor...</Typography>
        </Container>
      </>
    );
  }

  if (!type || !legalTextMap[type]) {
    return (
      <>
        <AppBar position="static" sx={{ backgroundColor: 'rgba(2, 36, 38, 1)' }}>
          <Toolbar>
            <Box component="img" src={logo} alt="logo" sx={{ height: 40, mr: 2 }} />
          </Toolbar>
        </AppBar>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{ mb: 2 }}
            >
              Geri Dön
            </Button>
          </Box>
          <Typography variant="h4" gutterBottom>
            Sayfa Bulunamadı
          </Typography>
          <Typography>Aradığınız yasal metin bulunamadı.</Typography>
        </Container>
      </>
    );
  }

  return (
    <>
      <AppBar position="static" sx={{ backgroundColor: 'rgba(2, 36, 38, 1)' }}>
        <Toolbar>
          <Box component="img" src={logo} alt="logo" sx={{ height: 40, mr: 2 }} />
        </Toolbar>
      </AppBar>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mb: 2 }}
          >
            Geri Dön
          </Button>
        </Box>
        <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
          {legalTextMap[type]}
        </Typography>
        <Paper sx={{ p: 3 }}>
        <Box
          dangerouslySetInnerHTML={{ __html: content }}
          sx={{
            '& p': {
              marginBottom: 2,
              lineHeight: 1.6,
              fontSize: '14px',
            },
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              marginTop: 3,
              marginBottom: 2,
              fontWeight: 'bold',
              color: '#333',
            },
            '& ul, & ol': {
              marginBottom: 2,
              paddingLeft: 3,
            },
            '& li': {
              marginBottom: 1,
              fontSize: '14px',
            },
            '& table': {
              width: '100%',
              borderCollapse: 'collapse',
              marginBottom: 2,
            },
            '& th, & td': {
              border: '1px solid #ddd',
              padding: '8px',
              textAlign: 'left',
              fontSize: '14px',
            },
            '& th': {
              backgroundColor: '#f5f5f5',
              fontWeight: 'bold',
            },
            '& a': {
              color: '#1976d2',
              textDecoration: 'underline',
            },
            '& strong, & b': {
              fontWeight: 'bold',
            },
            '& em, & i': {
              fontStyle: 'italic',
            },
          }}
        />
      </Paper>
    </Container>
    </>
  );
}
